import { useMemo, useState, useEffect, useContext, useRef } from 'react'
import { RhyBusButton } from '@rhybus/button'
import { RhyBusCard } from '@rhybus/card'
import { RhyBusDiscussion } from '@rhybus/discussion'
import { RhyBusFiles } from '@rhybus/files'
import { RhyBusPageHeader } from '@rhybus/header'
import { RhyBusProgressBar } from '@rhybus/progress-bar'
import { RhyBusTeamMembers } from '@rhybus/team-members'
import { useParams, useNavigate } from 'react-router-dom'
import { rhybusRListsHttp } from '../../api/rhybus-list-http'
import { getTrainingTrackDetailByGUID, getTrainingTrackLinesByGUID } from '../../services/listview'
import {
    handleDetailAddFolder,
    handleDetailDeleteFiles,
    handleDetailDeleteSingleFile,
    handleDetailDownloadAllFiles,
    handleDetailDownloadFile,
    handleDetailUpdateFolderName,
    handleDetailUploadFiles,
    loadDetailFiles,
} from '../../shared/utility/fileHelpers'
import { addDiscussionToSharePoint, addReplyToSharePoint, addTeamMember, buildDetailProgressSteps, deleteTeamMember, fetchDiscussionsByGUID, fetchTeamMembersByGUID, getTeamMemberPeopleSuggestions, searchDiscussions, separateDiscussionsAndStatusNotes } from '../../services/detailview'
import { rhybusWhoHttp } from '../../api/rhybus-who-http'
import { UserContext } from '../../store/UserContextProvider'
import { UserDetailsContext } from '../../utils/user-context'
import PageOverlay from '../../utils/controls/pageoverLay/pageoverLay'
import { triggerCaseNotifications, ACTIVITY_ID } from '../../services/notificationUtils'
import { STATUS_CODE_TRAINIG } from '../../config/configuration'

import {
    DELETE_MSG,
    FILE_HINT_MSG,
    SELECT_ONE_ITEM,
    ListNames,
    TRAINING_PROGRESS_TEMPLATE,
    TRAINING_PROGRESS_FLOW,
    addDiscussionPopupConfig,
    replyPopupConfig,
    DETAIL_VIEW_NEW_PEOPLE_SUGGESTIONS,
    DETAIL_VIEW_NEW_TEAM_MEMBERS,
    type DetailViewComment,
    type DetailViewFileItem,
    type DetailViewPerson,
} from '../../config/configuration'
import './detail-view.css'
import StudentTable from '../form/form-table'
import TrainingActionButtons from './action-buttons/ActionButtons'

const headerColumns = [
    { key: 'training', label: 'Training' },
    { key: 'suffix', label: 'Suffix' },
    { key: 'tMasl', label: 'T-MASL' },
    { key: 'location', label: 'Location' },
    { key: 'exa', label: 'EXA' },
    { key: 'priority', label: 'Priority' },
    { key: 'reportDate', label: 'Report Date', type: 'date' },
    { key: 'startDate', label: 'Start Date', type: 'date' },
    { key: 'endDate', label: 'End Date', type: 'date' },
    { key: 'duration', label: 'Duration' },
    { key: 'classId', label: 'Class ID' },
    { key: 'status', label: 'Status' },
    { key: 'financialStatus', label: 'Financial Status' },
    { key: 'facilityCategory', label: 'Facility Category' },
]

type LocalFolderItem = {
    ID: string
    Id: string
    title: string
    is_active: string
    count: number
    Files: DetailViewFileItem[]
}

const cloneFile = (file: DetailViewFileItem): DetailViewFileItem => ({ ...file })

const cloneComment = (comment: DetailViewComment): DetailViewComment => ({
    ...comment,
    ItemCreatedBy: { ...comment.ItemCreatedBy },
})

const clonePerson = (person: DetailViewPerson): DetailViewPerson => ({ ...person })

const filterComments = (comments: DetailViewComment[], query: string) => {
    const searchQuery = query.trim().toLowerCase()
    if (!searchQuery) return comments

    return comments.filter((comment) => {
        return [comment.Subject, comment.Comment, comment.CommentTo, comment.DocumentName, comment.ItemCreatedBy?.Title]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(searchQuery))
    })
}

export default function TrainingDetailView() {
    const [discussionSearchText, setDiscussionSearchText] = useState('')
    const { me } = useContext(UserContext)
    const { roles } = useContext(UserDetailsContext) as any
    const userRole = Array.isArray(roles) ? (roles as string[]).join(', ') : ''
    const [files, setFiles] = useState<DetailViewFileItem[]>([])
    const [folders, setFolders] = useState<LocalFolderItem[]>([])
    const [isFilesLoading, setIsFilesLoading] = useState(false)
    const [loading, setLoading] = useState(false)
    const [isDiscussionLoading, setIsDiscussionLoading] = useState(false)
    const [dataLoading, setDataLoading] = useState(true)
    const [fileError, setFileError] = useState<string | null>(null)
    const [folderError, setFolderError] = useState<string | null>(null)
    const [discussions, setDiscussions] = useState<DetailViewComment[]>()
    const [allDiscussions, setAllDiscussions] = useState<any[]>([])
    const [statusNotes, setStatusNotes] = useState<any[]>([])
    const [peoplePickerSuggestions, setPeoplePickerSuggestions] = useState<DetailViewPerson[]>([])
    const [teamMembers, setTeamMembers] = useState<DetailViewPerson[]>(DETAIL_VIEW_NEW_TEAM_MEMBERS.map(clonePerson))
    const [key, setKey] = useState<any>()
    const [teamPeoplePickerSuggestions, setTeamPeoplePickerSuggestions] = useState<DetailViewPerson[]>([])
    const [currentTeamMembersListID, setCurrentTeamMembersListID] = useState<number | null>(null)
    const [filesComponentKey, setFilesComponentKey] = useState(0)
    const [trainingData, setTrainingData] = useState<any>(null)
    const [trainingLines, setTrainingLines] = useState<any[]>([])
    const [historyItems, setHistoryItems] = useState<any[]>([])
    const navigate = useNavigate()
    const { itemGuid } = useParams()
    const detailItemKey = useMemo(() => trainingData?.eKey || itemGuid || '', [trainingData, itemGuid])
    const trainingTrackGUID = useMemo(() => trainingData?.trainingTrackGUID ?? '', [trainingData])

    const rawFileCache = useRef<Map<string, File>>(new Map())
    useEffect(() => {
        const capture = (e: Event) => {
            const input = e.target as HTMLInputElement
            if (input.type !== 'file' || !input.files?.length) return
            Array.from(input.files).forEach((f) => rawFileCache.current.set(`${f.name}_${f.size}`, f))
        }
        const captureDrop = (e: DragEvent) => {
            const files = e.dataTransfer?.files
            if (!files?.length) return
            Array.from(files).forEach((f) => rawFileCache.current.set(`${f.name}_${f.size}`, f))
        }
        document.addEventListener('change', capture, true)
        document.addEventListener('drop', captureDrop, true)
        return () => {
            document.removeEventListener('change', capture, true)
            document.removeEventListener('drop', captureDrop, true)
        }
    }, [])

    const loadFiles = () => loadDetailFiles(detailItemKey, setFiles, setFolders, setIsFilesLoading, setFileError, 'training-detail-view')

    useEffect(() => {
        if (!trainingData) return
        loadFiles()
        getTeamMembers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailItemKey, trainingData])

    const allFiles = useMemo(() => [...files, ...folders.flatMap((folder) => folder.Files)], [files, folders])
    const allFileNames = useMemo(() => allFiles.map((file) => file.Name).filter(Boolean), [allFiles])
    const trainingTrackSummaryFields = [
        { label: 'User Case ID', value: trainingData?.userCaseId || '', },
        { label: 'Line', value: trainingData?.line || '', },
        { label: 'Type', value: trainingData?.type || '', },
        { label: 'WCN', value: trainingData?.wcn || '', },
        { label: 'Priority', value: trainingData?.priority || '', },
        { label: 'Fiscal Year', value: trainingData?.fiscalYear || '', },
        { label: 'Quarter', value: trainingData?.quarter || '', },
        { label: 'Rank', value: trainingData?.rank || '', },
        { label: 'TPC', value: trainingData?.tpc || '', },
        { label: 'Training Person First Name', value: trainingData?.trainingPersonFirstName || '', },
        { label: 'Training Person Middle Name', value: trainingData?.trainingPersonMiddleName || '', },
        { label: 'Training Person Last Name', value: trainingData?.trainingPersonLastName || '', },
        { label: 'Vetted', value: trainingData?.vetted || '', },
    ]
    const refreshHistory = async () => {
        if (!trainingTrackGUID) return
        try {
            const historyList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_HISTORY)
            const all = [...(await historyList.query().get<any>())]
            setHistoryItems(all.filter((h: any) => h.TrainingTrackGUID === trainingTrackGUID))
        } catch (err) {
            console.error('[refreshHistory]', err)
        }
    }

    const refetchTrainingData = async () => {
        if (!itemGuid) return
        setDataLoading(true)
        try {
            const data = await getTrainingTrackDetailByGUID(itemGuid)
            setTrainingData(data)
            if (data?.trainingTrackGUID) {
                const lines = await getTrainingTrackLinesByGUID(data.trainingTrackGUID)
                setTrainingLines(lines)
            }
        } finally {
            setDataLoading(false)
        }
    }

    const handleGenerateFundingSuccess = async () => {
        setLoading(true)
        await Promise.all([refetchTrainingData(), refreshHistory(), getDiscussions()])
        setLoading(false)
    }

    const [isCanceled, setIsCanceled] = useState(false)
    const handleCancelSuccess = async () => {
        setIsCanceled(true)
        setLoading(true)
        await Promise.all([refetchTrainingData(), refreshHistory(), getDiscussions()])
        setLoading(false)
    }

    useEffect(() => {
        if (!trainingTrackGUID) return
        getDiscussions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trainingTrackGUID])
    useEffect(() => {
        if (!trainingTrackGUID) return
        refreshHistory()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trainingTrackGUID])
    useEffect(() => {
        if (!itemGuid) return
        refetchTrainingData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemGuid])
    const handleDiscussionSearch = (event: { target: { value: string } }) => {
        const inputValue = event.target.value
        setDiscussionSearchText(inputValue)
        const filtered = searchDiscussions(inputValue.toLowerCase(), allDiscussions)
        const { discussions: disc, statusNotes: notes } = separateDiscussionsAndStatusNotes(filtered)
        setDiscussions(disc)
        setStatusNotes(notes)
    }
    const getDiscussions = async () => {
        const items = await fetchDiscussionsByGUID(trainingTrackGUID)
        setAllDiscussions(items)
        const { discussions: disc, statusNotes: notes } = separateDiscussionsAndStatusNotes(items)
        setDiscussions(disc)
        setStatusNotes(notes)
    }
    const handleAddDiscussion = async (discussionData: any) => {
        setIsDiscussionLoading(true)
        try {
            await addDiscussionToSharePoint(itemGuid, discussionData, me)
            await getDiscussions()
            const trainingTrackId = trainingData?.trainingTrackId ?? trainingTrackGUID
        await triggerCaseNotifications(
            trainingTrackGUID,
            //`Training ${trainingTrackId} - A new discussion has been added by ${me?.profileName}`,
            `${me?.profileName} added a discussion comment on Training Track ${trainingTrackId}.`,
            ACTIVITY_ID.TrainingDiscussion,
            STATUS_CODE_TRAINIG.seatValidation,
            me?.email,
        )
        } finally {
            setIsDiscussionLoading(false)
        }
    }
    const handleAddReply = async (replyData: any) => {
        setIsDiscussionLoading(true)
        try {
            await addReplyToSharePoint(itemGuid, replyData, me)
            await getDiscussions()
        } finally {
            setIsDiscussionLoading(false)
        }
    }

    const handlePeoplePickerChange = async (value: string) => {
        if (!value) {
            setPeoplePickerSuggestions([])
            return
        }

        setPeoplePickerSuggestions(
            DETAIL_VIEW_NEW_PEOPLE_SUGGESTIONS.filter((person) => person.Title.toLowerCase().includes(value.toLowerCase()))
        )
    }

    const handleUploadFiles = (uploadedFiles: any[], folderName?: string) => {
        const rawFiles = uploadedFiles
            .map((f: any) => {
                const cached = rawFileCache.current.get(`${f.name}_${f.size}`)
                if (cached instanceof File) return cached
                if (f.file instanceof File) return f.file
                return null
            })
            .filter((f): f is File => f instanceof File)
        if (!rawFiles.length) return
        const existingETagMap = new Map<string, string>()
        // Only search the correct location to avoid sending a root-blob eTag for a folder
        // blob (or vice versa) — the server rejects an eTag that doesn't match the path.
        const eTagSource: any[] = folderName
            ? (folders.find((folder: any) => folder.title === folderName)?.Files ?? [])
            : files
        rawFiles.forEach((f) => {
            const match = eTagSource.find((af: any) => af.Name === f.name)
            if (match?.eTag) existingETagMap.set(f.name, match.eTag)
        })
        return handleDetailUploadFiles(rawFiles, detailItemKey, setIsFilesLoading, setFileError, loadFiles, folderName, me, 'training-detail-view', existingETagMap)
            .then((uploadSucceeded) => {
                rawFiles.forEach((f) => rawFileCache.current.delete(`${f.name}_${f.size}`))
                if (uploadSucceeded) {
                    if (!folderName) {
                        setFiles((prev: any[]) => {
                            const missing = rawFiles.filter((f) => !prev.some((p: any) => p.Name === f.name))
                            return missing.length ? [...prev, ...missing.map((f) => ({ Name: f.name, Title: f.name }))] : prev
                        })
                    } else {
                        setFolders((prev: any[]) =>
                            prev.map((folder: any) =>
                                folder.title === folderName
                                    ? {
                                        ...folder,
                                        Files: [
                                            ...folder.Files,
                                            ...rawFiles
                                                .filter((f: File) => !folder.Files.some((p: any) => p.Name === f.name))
                                                .map((f: File) => ({ Name: f.name, Title: f.name })),
                                        ],
                                    }
                                    : folder
                            )
                        )
                    }
                }
                setFilesComponentKey((k) => k + 1)
            })
    }

    const handleDeleteFiles = (selectedFiles: Array<{ Name: string }>, folderName?: string) =>
        handleDetailDeleteFiles(selectedFiles, detailItemKey, setIsFilesLoading, setFileError, loadFiles, folderName, folders.map((f) => f.title), 'training-detail-view')

    const handleDownloadFile = (file: any) =>
        handleDetailDownloadFile(file, detailItemKey, 'training-detail-view')

    const handleDownloadAllFiles = (filesToDownload: any[]) =>
        handleDetailDownloadAllFiles(filesToDownload, detailItemKey, 'training-detail-view')

    const handleDeleteSingleFile = (fileToDelete: any) =>
        handleDetailDeleteSingleFile(fileToDelete, detailItemKey, setIsFilesLoading, setFileError, loadFiles, 'training-detail-view')

    const handleAddFolder = (folderName: string) => {
        setFolderError(null)
        const folderPath = `/${folderName}`
        setFolders((prev) => [
            ...prev,
            { ID: folderPath, Id: folderPath, title: folderName, is_active: 'true', count: 0, Files: [] },
        ].sort((a, b) => a.title.localeCompare(b.title)))
        void handleDetailAddFolder(folderName, detailItemKey, setIsFilesLoading, setFolderError, loadFiles, 'training-detail-view')
    }

    const handleUpdateFolderName = (oldName: string, newName: string) =>
        handleDetailUpdateFolderName(oldName, newName, setFolders)

    const getTeamMembers = async () => {
        const team = await fetchTeamMembersByGUID(trainingTrackGUID)
        if (team) {
            setTeamMembers(team.TeamMembers?.length > 0 ? team.TeamMembers : [])
            setKey(team)
            setCurrentTeamMembersListID(team?.eKey || team?.ID)
        } else {
            setTeamMembers([])
            setCurrentTeamMembersListID(null)
        }
    }

    const handleAddTeamMember = async (userToAdd: any, inputValues?: any) => {
        const validation = handleValidateUser(userToAdd)
        if (!validation.isValid) return
        setLoading(true)
        await addTeamMember(teamMembers, currentTeamMembersListID, trainingTrackGUID, userToAdd, trainingData?.trainingTrackId, me, key?.eKey ?? '', key?.eTag ?? '')
        getTeamMembers()
        setLoading(false)
    }

    const handleTeamPeoplePickerChange = async (value: string) => {
        if (!value) {
            setTeamPeoplePickerSuggestions([])
            return
        }
        const suggestions = await getTeamMemberPeopleSuggestions(value)
        setTeamPeoplePickerSuggestions(suggestions)
    }

    const handleValidateUser = (userData: any) => {
        if (!userData) return { isValid: true, error: '' }

        // Check by email if available (object case)
        const email = userData?.EMail ?? userData?.email ?? userData?.Mail ?? ''
        if (email) {
            const alreadyExists = teamMembers.some((member: any) =>
                member?.EMail?.toLowerCase() === email.toLowerCase()
            )
            if (alreadyExists) {
                return { isValid: false, error: 'User is already a team member' }
            }
        }

        const userId = typeof userData === 'object'
            ? String(userData?.Id ?? userData?.ID ?? userData?.userId ?? userData?.LoginName ?? '').trim()
            : String(userData ?? '').trim()
        if (userId) {
            const alreadyExists = teamMembers.some((member: any) =>
                String(member?.ID ?? member?.Id ?? member?.userId ?? member?.LoginName ?? '').trim() === userId
            )
            if (alreadyExists) {
                return { isValid: false, error: 'User is already a team member' }
            }
        }

        return { isValid: true, error: '' }
    }

    const handleDeleteTeamMembers = async (selectedMembers: DetailViewPerson[]) => {
        setLoading(true)
        await deleteTeamMember(teamMembers, selectedMembers, currentTeamMembersListID, trainingTrackGUID, me, key?.eKey ?? '', key?.eTag ?? '')
        await getTeamMembers()
        setLoading(false)
    }

    const handleSearchTeamPeoplePicker = (event: any, setUserToAdd: (user: any) => void, setInputValues: (val: any) => void) => {
        // Convert the suggestion object to a user object with required properties
        const userObject = {
            Id: event?.Key ?? event?.rid ?? event?.userId ?? '',
            LoginName: event?.Key ?? event?.rid ?? event?.userId ?? event?.email ?? '',
            Email: event?.email ?? '',
            EMail: event?.email ?? '',
            Title: event?.DisplayText ?? event?.persona ?? event?.displayName ?? '',
            IsResolved: true,

        }
        setInputValues(userObject)
        setUserToAdd(userObject)
        setTeamPeoplePickerSuggestions([])
    }
    const showBtn = me?.profileName?.trim().toLowerCase() !== trainingData?.ItemCreatedBy?.trim().toLowerCase()

    return (
        <main id="main-content">
            {(dataLoading || loading || isFilesLoading || isDiscussionLoading) && <PageOverlay />}

            <RhyBusPageHeader
                pageHeaderClassName='detail-view-header'
                pageTitle=''
                headerLeftContent={
                    <div className="rb-page-header-title" id="pagetitles">
                        <h1 tabIndex={0} aria-live="polite" style={{ fontWeight: 700 }}>
                            <span className='darktext'>TRAINING TRACK ID:</span>
                            <span className='color-primary'>{trainingData?.trainingTrackId}</span>
                        </h1>
                    </div>
                }
                headerRightContent={
                    <div className='d-flex align-items-center gap-2 flex-wrap justify-content-end detail-view-actions detail-view-static-actions'>
                        <TrainingActionButtons
                            trainingTrackGUID={trainingTrackGUID}
                            trainingTrackId={trainingData?.trainingTrackId}
                            currentStatusCode={trainingData?.StatusCode}
                            userRole={userRole}
                            me={me}
                            showBtn={showBtn && !isCanceled && Number(trainingData?.StatusCode) !== 3}
                            onSuccess={handleGenerateFundingSuccess}
                            onCancelSuccess={handleCancelSuccess}
                        />
                    </div>
                }
            />

            <div className='container'>
                <RhyBusCard
                    className='details-summary'
                    cardBody={
                        <>
                            <div className='detail-view-progress detail-view-static-progress'>
                                <RhyBusProgressBar
                                    showItems={7}
                                    isArrow={false}
                                    progressBarType='horizontal'
                                    steps={buildDetailProgressSteps(historyItems, trainingData, TRAINING_PROGRESS_TEMPLATE, TRAINING_PROGRESS_FLOW)}
                                    trigger='hover'
                                    responsive={[
                                        { breakpoint: 0, show: 2 },
                                        { breakpoint: 576, show: 4 },
                                        { breakpoint: 992, show: 7 },
                                    ]}
                                />
                            </div>
                        </>
                    }
                />

                <div className='row'>
                    <div className='col-12 col-lg-8'>
                        <RhyBusCard
                            isTitleBorder={true}
                            className='training-detailview'
                            cardHeaderLeft={<div className='detail-view-card-title' tabIndex={0} aria-label='Training Track Details'>Training Track Details</div>}
                            cardHeaderRight={
                                <RhyBusButton
                                    label='Edit'
                                    leftIcon='icon-rb-pencil font-12'
                                    variant='text'
                                    size='md'
                                    className='inter-semibold font-13'
                                    onClick={() => navigate(`/createtraining/${itemGuid}`)}
                                />
                            }
                            cardBody={
                                <>
                                    <div className='row g-3'>
                                        {trainingTrackSummaryFields.map((field) => (
                                            <div tabIndex={0} aria-live='polite' className='col-12 col-md-3' key={field.label}>
                                                <div className='detail-view-field-label'>{field.label}</div>
                                                <div className='detail-view-field-value text-break'>{field.value || '-'}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className='mt-3' tabIndex={0} aria-live='polite'>
                                        <div className='detail-view-field-label'>Description</div>
                                        <p className='detail-view-description-text mb-0'>{trainingData?.description || '-'}</p>
                                    </div>
                                    <div className='col-12'>
                                        <div className='border-radius-2 border-1 overflow-hidden mt-3'>
                                            <div className='form-header-bgcolor whitetext font-14 px-3 py-2 inter-medium' tabIndex={0} aria-live='polite'>
                                                Trainings ({String(trainingLines.length).padStart(2, '0')})
                                            </div>
                                            <div className='training-track-table'>
                                                <StudentTable initialData={trainingLines} headerColumns={headerColumns} />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }
                        />

                        <div className='mt-3 discussions-section'>
                            <RhyBusDiscussion
                                allFileNames={allFileNames}
                                discussionsData={discussions}
                                statusNotesData={statusNotes}
                                onAddDiscussion={handleAddDiscussion}
                                onAddReply={handleAddReply}
                                peoplePickerSuggestions={peoplePickerSuggestions}
                                onPeoplePickerChange={handlePeoplePickerChange}
                                onResetPeoplePicker={() => setPeoplePickerSuggestions([])}
                                addDiscussionPopupConfig={addDiscussionPopupConfig}
                                replyConfig={replyPopupConfig}
                                isInternalUser={true}
                                showDiscussions={true}
                                showStatusNotes={true}
                                discusstionTabTitle='Discussions'
                                statusNotesTabTitle='Status Notes'
                                searchText={discussionSearchText}
                                onSearchChange={handleDiscussionSearch}
                                isBadge={true}
                                badgeType='text'
                                tabSize='xlarge'
                            />
                        </div>
                    </div>

                    <div className='col-12 col-lg-4'>
                        <div className='detail-view-sidebar-stack'>
                            {/* {isFilesLoading && (
                                <div className='detail-view-files-status' role='status' aria-live='polite'>
                                    Loading files...
                                </div>
                            )} */}
                            <RhyBusFiles
                                key={filesComponentKey}
                                isFiles={true}
                                isFolders={true}
                                isFolderFiles={true}
                                canAddFolder={true}
                                canAddFileInFolder={true}
                                popoverSize='md'
                                isCheckbox={true}
                                isDownload={true}
                                isDownloadAll={true}
                                isGlobalDelete={allFiles.length > 0}
                                isFileLevelDelete={true}
                                isFileLevelDownload={true}
                                data={{ files, Folders: folders, Count: allFiles }}
                                canAddFile={true}
                                canDelete={allFiles.length > 0}
                                label='Files'
                                onUploadFiles={handleUploadFiles}
                                onAddFolder={handleAddFolder}
                                onDeleteFiles={handleDeleteFiles}
                                DELETE_MSG={DELETE_MSG}
                                SELECT_ONE_ITEM={SELECT_ONE_ITEM}
                                onDownload={handleDownloadFile}
                                onDownloadAll={handleDownloadAllFiles}
                                onDelete={handleDeleteSingleFile}
                                addLabel='Add'
                                downloadLabel='Download'
                                downloadAllLabel='Download All'
                                addFolderLabel='Add Folder'
                                note='Screenshots need to be saved locally before uploading into system.'
                                FILE_HINT_MSG={FILE_HINT_MSG}
                                fileError={fileError}
                                folderError={folderError}
                                onUpdateFolderName={handleUpdateFolderName}
                            />

                            <RhyBusTeamMembers
                                TeamMembersData={teamMembers}
                                peoplePickerData={teamPeoplePickerSuggestions}
                                onAddTeamMember={handleAddTeamMember}
                                onDeleteTeamMembers={handleDeleteTeamMembers}
                                onValidateUser={handleValidateUser}
                                onSearchPeoplePicker={handleSearchTeamPeoplePicker}
                                onPeoplePickerChange={handleTeamPeoplePickerChange}
                                tooltipsData={{ 'Team Members': 'Project and review participants' }}
                                teammembersIcon='icon-rb-poc font-24'
                                IsBadgeType='text'
                                labels={{
                                    teamMembers: 'Team Members',
                                    addButton: 'Add',
                                    popoverTitle: 'Add Team Member',
                                    okButton: 'OK',
                                    cancelButton: 'Cancel',
                                    deleteButton: 'Delete',
                                    teamMemberField: 'Team Member',
                                    teamMemberFieldTooltip: 'Select a person to add as a team member',
                                    pleaseEnterTeamMember: 'Please enter Team Member',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    )
}
